import { useState, useRef, useEffect } from 'react';
import {
    Button,
    Modal,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Divider,
    Typography,
    Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';


export default function AddItem({
    formFields,
    initialState,
    onFormSubmit,
    items,
    addTitle,
}) {
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState(initialState);
    const modalRef = useRef(null);
    const [isOverflow, setIsOverflow] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null)

    useEffect(() => {
        if (modalRef.current) {
            const hasOverflow = modalRef.current.scrollHeight > modalRef.current.clientHeight;
            if (!isOverflow && hasOverflow)
                setIsOverflow(hasOverflow);
            else setIsOverflow(false);
        }
    }, [open]);

    const handleInputChange = (e, fieldName) => {
        if (e.target) {
            let { name, value } = e.target;
            setFormData({
                ...formData,
                [name]: value,
            });
        } else {
            setFormData({
                ...formData,
                [fieldName]: moment(e._d).format('YYYY-MM-DD'),
            });
        }
    }

    function validateForm(formData) {
        const requiredFields = formFields.filter((formField) => formField.required === true)
        // There could be formData objects that don't yet have a required: true attribute
        if (requiredFields.length > 0) {
            // The first instance of an empty string in the formdata requiredFields should make the formData invalid
            const isValid = !requiredFields.some((requiredField) => (
                formData[requiredField.name] === ''
            ))
            return isValid
        } else return formData
    }

    function assignDefaultValues(formData) {
        const fieldsToReassign = ['percentageN', 'percentageP', 'percentageK']
        const defaultValue = 0
        // Don't map unless its Fertilizer
        if (formData[fieldsToReassign[0]]) {
            fieldsToReassign.forEach((fieldToReassign) => {
                // Is fertilizer empty?
                if (formData[fieldToReassign] === '') {
                    formData[fieldToReassign] = defaultValue
                }
            })
        } else return
    }

    const handleSubmit = () => {
        assignDefaultValues(formData)
        const validated = validateForm(formData)
        if (validated) {
            onFormSubmit(formData);
            setErrorMessage(null)
            setOpen(false);
            setFormData(initialState)
        }
        else {
            setErrorMessage('Please fill in all required fields')
            return
        }
    };

    function formatRequiredLabel(field) {
        if (field.required) {
            return `${field.label} (*)`
        } else return `${field.label}`
    }

    const renderFormField = field => {
        if (field.type === 'text') {
            return (
                <TextField
                    fullWidth
                    name={field.name}
                    label={formatRequiredLabel(field)}
                    value={formData[field.name] || ''}
                    onChange={handleInputChange}
                    type="text"
                />
            );
        } else if (field.type === 'number') {
            return (
                <TextField
                    fullWidth
                    name={field.name}
                    label={formatRequiredLabel(field)}
                    value={formData[field.name] || ''}
                    onChange={handleInputChange}
                    type="number"
                />
            )
        }
        else if (field.type === 'select') {
            return (
                <FormControl fullWidth>
                    <InputLabel sx={{ background: 'white' }}>{formatRequiredLabel(field)}</InputLabel>
                    <Select
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleInputChange}
                    >
                        {field.options.map((option, index) => (
                            <MenuItem key={index} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }
        else if (field.type === 'date') {
            return (
                <FormControl fullWidth>
                    {formatRequiredLabel(field)}
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker name={field.name} onChange={(e) => handleInputChange(e, field.name)} />
                    </LocalizationProvider>
                </FormControl>
            );
        }
    };

    return (
        <Box>
            <Button variant='contained' color='primary' onClick={() => setOpen(true)}>
                {addTitle ? addTitle : 'Add new'}
            </Button>
            <Divider sx={{ mt: '1rem' }} />
            <Modal
                ref={modalRef}
                open={open}
                onClose={() => {
                    setErrorMessage(null)
                    setOpen(false)
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Paper
                    sx={{
                        width: { xs: '100%', sm: 400, md: 500 },
                        maxWidth: '95vw',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        p: { xs: 2, sm: 3 },
                        m: 0,
                        boxSizing: 'border-box',
                    }}
                >
                    <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='h6' gutterBottom>
                            {addTitle ? addTitle : 'Add new item'}
                        </Typography>
                        <span style={{ fontSize: 15 }}>{`(*) is required`}</span>
                    </Box>
                    {formFields.map((field, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            {renderFormField(field)}
                        </Box>
                    ))}
                    {errorMessage && <Alert severity='error' sx={{ width: 'fit-content' }}>{errorMessage}</Alert>}
                    <Button style={{ marginTop: 8 }} variant='contained' onClick={handleSubmit}>
                        Submit
                    </Button>
                </Paper>
            </Modal>
            <List>
                {items.length > 0 && items.map((item, index) => (
                    <ListItem key={index}>
                        <ListItemText primary={item.machineName} secondary={item.purpose} />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}